var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import numeral from "numeral";
import "./App.scss";
import { useTranslation } from "react-i18next";
import { Suspense, useEffect, useMemo, useCallback } from "react";
import { fas } from "@fortawesome/pro-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import routes from "@/routes";
import { APP_REVISION_NO, PUBLIC_URL, SITE_TITLE } from "@/utils/const";
import { useLogger } from "@/utils/loggerService";
import { useTheme } from "@/utils/themes";
import { useDisplay } from "@/utils/display";
import { useColorScheme } from "@mui/joy";
import { useGetCustomizeAdminThemesQuery, useGetOutputImageQuery, useLoadSystemConfigurationsQuery, } from "./store/api";
import Status from "@/components/security/status";
import { setDisplaySize } from "./store/layoutSlice";
import { useAppDispatch, useAppSelector } from "./hooks/store";
import { DevMenu } from "@/components/dev/menu";
import { LoaderImage } from "./utils/loader";
import { updateThemeSettings } from "./store/themeSlice";
import { loadBGImage } from "./utils/funcs";
library.add(fas);
function App() {
    var router = useMemo(function () {
        return createBrowserRouter(routes, {
            basename: "".concat(PUBLIC_URL),
        });
    }, []);
    var dispatch = useAppDispatch();
    var _a = useTranslation(["common"]), t = _a.t, i18n = _a.i18n;
    var logger = useLogger().logger;
    //THEME COULEUR
    var configTheme = useGetCustomizeAdminThemesQuery(undefined).data;
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        if (!(configTheme === null || configTheme === void 0 ? void 0 : configTheme.data))
            return;
        var configs = configTheme.data;
        dispatch(updateThemeSettings({
            colorBase: configs.theme_color,
            midColor: (_a = configs.colorPalette) === null || _a === void 0 ? void 0 : _a.midColor,
            endColor: (_b = configs.colorPalette) === null || _b === void 0 ? void 0 : _b.endColor,
            fontOpacity: configs.font_opacity_settings,
            colors_palette: configs.colors_palette,
            bgImage: configs.background_image,
            hasDefault: (_e = (_d = (_c = configs === null || configs === void 0 ? void 0 : configs.font_opacity_settings) === null || _c === void 0 ? void 0 : _c.widget) === null || _d === void 0 ? void 0 : _d.default) !== null && _e !== void 0 ? _e : false,
        }));
    }, [configTheme, dispatch]);
    var bgImage = useAppSelector(function (state) { return state.theme; }).bgImage;
    var fileName = bgImage.file;
    var file = useGetOutputImageQuery({
        params: {
            id: "bgImage",
            type: "Administration",
            isTempFile: false,
            tempName: fileName,
            isRecordFile: false,
        },
    }, { skip: !fileName }).data;
    useEffect(function () {
        var _a;
        if (!bgImage)
            return;
        loadBGImage(bgImage, (_a = file === null || file === void 0 ? void 0 : file.data) === null || _a === void 0 ? void 0 : _a.output);
    }, [bgImage, file, loadBGImage]);
    /**
     * Enregistrer une chaine de traduction
     *
     * @param locale Code de traduction à enregistrer
     * @param t Fonction de traduction
     */
    var registerNumeralLocale = useCallback(function (locale, t) {
        try {
            if (numeral.locales[locale] || numeral.locales[locale.toLowerCase()]) {
                return;
            }
            delete numeral.locales[locale];
            delete numeral.locales[locale.toLowerCase()];
            var configuration = {
                delimiters: {
                    thousands: t("settings.number.delimiters.thousands", {
                        ns: "common",
                    }),
                    decimal: t("settings.number.delimiters.decimal", { ns: "common" }),
                },
                abbreviations: {
                    thousand: t("settings.number.abbreviations.thousand", {
                        ns: "common",
                    }),
                    million: t("settings.number.abbreviations.million", {
                        ns: "common",
                    }),
                    billion: t("settings.number.abbreviations.billion", {
                        ns: "common",
                    }),
                    trillion: t("settings.number.abbreviations.trillion", {
                        ns: "common",
                    }),
                },
                ordinal: function (num) {
                    return num.toString();
                },
                currency: {
                    symbol: t("settings.number.currency.symbol", { ns: "common" }),
                },
            };
            logger.debug("Enregistrement des paramètres de traduction des nombres.");
            numeral.register("locale", locale, configuration);
        }
        catch (e) {
            logger.error("Erreur d'enregistrement des param\u00E8tres de traduction des nombres. ".concat(e.message));
        }
    }, [logger]);
    // Memoize display callback
    var handleDisplayChange = useCallback(function (bp) { return dispatch(setDisplaySize(bp.size)); }, [dispatch]);
    var _b = useDisplay(handleDisplayChange), attachEvents = _b.attachEvents, detachEvents = _b.detachEvents;
    useEffect(function () {
        attachEvents();
        return function () {
            detachEvents();
        };
    }, [attachEvents, detachEvents]);
    var _c = useTheme(), darkMode = _c.darkMode, variables = _c.variables;
    var _d = useColorScheme(), mode = _d.mode, setMode = _d.setMode;
    // Optimize theme effect
    useEffect(function () {
        var root = document.documentElement;
        root.classList.toggle("dark", darkMode);
        var newMode = darkMode ? "dark" : "light";
        logger.debug("[Theme] - Set UI theme ".concat(newMode));
        setMode(newMode);
        logger.debug("[Theme] - Set mode : ".concat(newMode));
    }, [darkMode, setMode, logger]);
    // Optimize CSS variables effect
    useEffect(function () {
        var root = document.documentElement;
        Object.entries(variables).forEach(function (_a) {
            var property = _a[0], value = _a[1];
            root.style.setProperty(property, value);
        });
        logger.debug("[Theme] - Set variables", variables);
    }, [variables, logger]);
    useEffect(function () {
        document.title = SITE_TITLE;
        document.documentElement.lang = i18n.language || "fr";
        logger.debug("[APP] - Modification de la langue de la page en ".concat(i18n.language, "."));
    }, [i18n.language, logger]);
    useEffect(function () {
        registerNumeralLocale(i18n.language, t);
        numeral.locale(i18n.language);
    }, [i18n.language, t, registerNumeralLocale]);
    var _e = useLoadSystemConfigurationsQuery(null, {
        // Add refetch options to prevent unnecessary requests
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: true,
    }), data = _e.data, isLoading = _e.isLoading, isFetching = _e.isFetching, isSuccess = _e.isSuccess, isError = _e.isError, error = _e.error;
    var statusProps = useMemo(function () { return ({
        code: "500",
        bgColor: "bg-red-100 dark:bg-red-900",
        textColor: "text-gray-500 dark:text-white",
    }); }, []);
    return (_jsxs("div", __assign({ className: "relative bg-[color:var(--colors-app-bg:#--colors-sky-50)] w-full h-full overflow-hidden" }, { children: [_jsx(Suspense, __assign({ fallback: _jsx(LoaderImage, {}) }, { children: isLoading || isFetching ? (_jsx(LoaderImage, {})) : isSuccess ? (_jsx(RouterProvider, { router: router })) : (_jsx(Status, __assign({}, statusProps))) })), APP_REVISION_NO && (_jsx("i", __assign({ className: "absolute text-xs text-red-700 bottom-4 right-6" }, { children: APP_REVISION_NO }))), _jsx(DevMenu, {})] })));
}
export default App;
